import * as Sentry from "@sentry/react";
import { toast } from "react-toastify";

// Curated 4xx messages come from the backend by convention (hand written English
// sentences the user can act on). Laravel FormRequest failures return a per-field
// `errors` map, which we route inline via onFieldErrors when a caller opts in so
// the user sees each error next to the offending field. 5xx, network errors,
// and malformed responses get a friendly fallback toast and are captured in
// Sentry with the request context. Endpoints that never populate a message body
// on a rejection fall through to the fallback rather than surfacing axios' own
// "Request failed with status code X" default.
export function showMutationError(error, options = {}) {
  const { fallback = "Something went wrong. Please try again.", onFieldErrors } = normaliseOptions(options);
  const status = error?.response?.status ?? null;
  const isClientError = status !== null && status >= 400 && status < 500;
  const fieldErrors = extractFieldErrors(error);
  const serverMessage = extractServerMessage(error);

  if (isClientError && fieldErrors && onFieldErrors) {
    onFieldErrors(fieldErrors);
    toast.error("Please fix the highlighted fields.");
    return;
  }

  if (serverMessage) {
    toast.error(serverMessage);
    if (!isClientError) {
      // A 5xx that still carries a curated message is a domain rejection
      // dressed as a server error. Surface it to the user, but also report it
      // to Sentry so the dev team sees the underlying crash context.
      captureToSentry(error, status);
    }
    return;
  }

  toast.error(fallback);
  captureToSentry(error, status);
}

function captureToSentry(error, status) {
  Sentry.captureException(error, {
    tags: { source: "mutation", status: status ?? "unknown" },
    extra: {
      url: error?.config?.url ?? null,
      method: error?.config?.method ?? null,
    },
  });
}

// Preserve the original two-argument signature (error, fallbackString) so the
// helper stays a drop in replacement for the older toast.error calls.
function normaliseOptions(options) {
  if (typeof options === "string") {
    return { fallback: options };
  }
  return options ?? {};
}

// Only accept a curated body-level message. Axios' default `error.message`
// (e.g. "Request failed with status code 500") is deliberately not used because
// it is not written for a user to read.
function extractServerMessage(error) {
  const data = error?.response?.data;
  if (!data) return null;
  const candidate = typeof data.message === "string" && data.message.length > 0
    ? data.message
    : (typeof data.error === "string" && data.error.length > 0 ? data.error : null);
  return candidate;
}

// Build a field-error clearer bound to a specific setState updater. Callers
// keep their own state; this just removes the duplicated closure that every
// per-field onChange would otherwise reinvent.
export function makeFieldErrorClearer(setFieldErrors) {
  return (key) => {
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };
}

function extractFieldErrors(error) {
  const raw = error?.response?.data?.errors;
  if (!raw || typeof raw !== "object") {
    return null;
  }

  const flat = {};
  for (const [key, value] of Object.entries(raw)) {
    const firstMessage = Array.isArray(value) ? value[0] : value;
    if (typeof firstMessage === "string" && firstMessage.length > 0) {
      flat[key] = firstMessage;
    }
  }

  return Object.keys(flat).length > 0 ? flat : null;
}
